import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, Choice, Icon, Input, Modal, useToast } from '@efira/ui';

import { useCatalog, usePreflight } from '../../api/queries';
import type { CatalogGame } from '../../api/queries';
import { useConsoleSession } from '../session';

/**
 * Смена игры на ходу.
 *
 * ГЛАВНОЕ СВОЙСТВО - НОВАЯ ИГРА НАЧИНАЕТСЯ СРАЗУ, и эфир при этом не
 * прерывается: подключение к TikTok держит сервер, а не сцена, и смена
 * сцены не трогает ни соединение вендора, ни счётчики эфира, ни ленту.
 * Ровно это и утверждает сценарий `e2e/console-switcher.spec.ts` - без него
 * обещание в подзаголовке окна остаётся обещанием.
 *
 * ПОЧЕМУ ОТДЕЛЬНОЕ ОКНО, А НЕ КАТАЛОГ. Каталог - это выбор до эфира, с
 * описаниями и карточкой игры; здесь человек уже в эфире, знает, что хочет,
 * и ему нужна сетка и поиск. Поэтому поиск свой, отдельный от каталога:
 * запрос, набранный в каталоге час назад, к смене игры отношения не имеет.
 */
function ruTitle(value: Record<string, string>, fallback: string): string {
  return value['ru'] ?? value['en'] ?? fallback;
}

export function Switcher() {
  const catalog = useCatalog();
  const session = useConsoleSession();
  const navigate = useNavigate();
  const toast = useToast();

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  /** Игра, на которую собираются переключиться прямо из эфира. */
  const [ask, setAsk] = useState<string | null>(null);

  const preflight = usePreflight();
  /*
   * ЭФИР - ЭТО TIKTOK, А НЕ СИМУЛЯТОР.
   *
   * Подтверждение стоит там, где смена игры видна ЗРИТЕЛЯМ и обнуляет
   * счёт настоящего раунда. У симулятора зрителей нет, терять нечего, а
   * вопрос на каждую смену игры во время репетиции - препятствие на
   * ровном месте: репетируют как раз переключением.
   */
  const live =
    preflight.data?.connection.status === 'live' && preflight.data.connection.sourceId === 'tiktok';

  /** Собственно переключение: одно место на оба пути - тихий и с вопросом. */
  const switchTo = (gameId: string) => {
    const game = (catalog.data?.games ?? []).find((item) => item.id === gameId);

    session.start(gameId);
    setAsk(null);
    toast.show({
      tone: 'success',
      title: `Игра сменилась: ${game === undefined ? gameId : ruTitle(game.title, game.id)}`,
      text: 'Эфир не прерывался — подключение и счётчики на месте.',
    });
    void navigate('/app');
  };

  /* Показываются только ГОТОВЫЕ игры: карточка игры, которой нет, - это
     обещание, данное сеткой и не выполненное продуктом. */
  const games = (catalog.data?.games ?? []).filter((game) => game.status === 'ready');
  const categories = catalog.data?.categories ?? [];

  const search = query.trim().toLowerCase();

  const matches = (game: CatalogGame) =>
    search === '' ||
    ruTitle(game.title, game.id).toLowerCase().includes(search) ||
    ruTitle(game.tagline, '').toLowerCase().includes(search);

  const found = games.filter(
    (game) => matches(game) && (category === 'all' || game.category === category),
  );

  /* Счётчики считаются ПО НАШЕМУ КАТАЛОГУ и по текущему запросу: число в
     пилюле обязано совпадать с тем, что человек увидит, нажав её. */
  const counts = new Map<string, number>([['all', games.filter(matches).length]]);

  for (const item of categories) {
    counts.set(item.id, games.filter((game) => matches(game) && game.category === item.id).length);
  }

  return (
    /* Имя пометки - словарём реестра (`switcher.modal`): окно меняет игру
       на ходу, и ворота честности требуют назвать его поимённо. */
    <div data-switcher="modal" className="flex min-h-[20rem] flex-col gap-3">
      {/* Две коротких фразы, а не перечисление из трёх сущностей: строка
          под заголовком - ремарка, а не справка. */}
      <p data-switcher="promise" className="text-foreground/90 text-sm">
        Новая игра начинается сразу. Подключение к TikTok не прерывается.
      </p>

      <div
        data-switcher="controls"
        data-control-row="switcher-controls"
        className="@container flex flex-wrap items-center gap-2"
      >
        <label className="relative flex min-w-0 flex-1 items-center text-sm">
          <span className="sr-only">Поиск по играм</span>
          {/* Лупа в поле: без неё поле опознаётся как поиск только по
              тексту подсказки. */}
          <span data-inside-field className="pointer-events-none absolute start-3 flex">
            <Icon name="search" size="sm" tone="muted" />
          </span>
          <Input
            data-switcher="search"
            placeholder="Найти игру"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            className="ps-9"
          />
        </label>
      </div>

      {/* Пилюли категорий со счётчиками. Счётчик - часть кнопки, а не
          подписи: «Викторины 3» подписью быть перестаёт. */}
      <Choice
        label="Категория игр"
        value={category}
        onChange={setCategory}
        size="sm"
        data-switcher="categories"
        className="flex-wrap"
        options={[{ id: 'all', title: { ru: 'Все' } }, ...categories].map((item) => ({
          value: item.id,
          data: { 'data-switcher-category': item.id },
          label: (
            <>
              {ruTitle(item.title as Record<string, string>, item.id)}
              {/* Счётчик - приглушённым ТЕКСТОМ, а не плашкой в плашке:
                  тёмная цифра на тёмном боксе на тёмной пилюле - три слоя
                  одного тона и контраст ниже AA. */}
              <span
                data-switcher-count={item.id}
                className="text-foreground/70 ms-2 text-xs tabular-nums"
              >
                {counts.get(item.id) ?? 0}
              </span>
            </>
          ),
        }))}
      />

      {found.length === 0 ? (
        <p data-switcher="empty" className="text-foreground/90 text-sm">
          По запросу ничего нет. Попробуйте короче — или снимите фильтр категории.
        </p>
      ) : (
        /* Прокручивается ТОЛЬКО сетка: шапка с поиском и категориями
           остаётся на месте, иначе фильтр уезжает вместе со списком. */
        <ul
          data-switcher="grid"
          className="grid min-h-0 flex-1 grid-cols-2 content-start gap-3 sm:grid-cols-3 lg:grid-cols-4"
        >
          {found.map((game) => {
            const current = game.id === session.gameId;

            return (
              <li key={game.id}>
                {/*
                  ПЛИТКА ИГРЫ - РОЛЬ «ПЛИТКА КАТАЛОГА», то есть `Card`, а не
                  `Button`.

                  До единицы 17 карточка была кнопкой пресета. У кнопки
                  ФИКСИРОВАННАЯ высота (`h-9` у размера по умолчанию), а
                  внутри лежали обложка, название и категория: содержимое не
                  помещалось, `overflow-hidden` его срезал, и во всём окне
                  не было видно ни одной обложки, а категория обрывалась
                  посередине строки (аудит единицы 16, находка 01).

                  Приём тот же, что у плитки каталога в центре консоли:
                  `Card asChild` с голым `button` внутри. Нажимается плитка
                  целиком, роль остаётся за карточкой.
                */}
                <Card
                  asChild
                  size="sm"
                  className={`group relative gap-0 py-0 pb-2 text-left ${
                    current ? 'border-primary border-2' : ''
                  }`}
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                      требует голого элемента: это тот самый случай, ради
                      которого он у компонента есть. Роль остаётся за Card. */}
                  <button
                    type="button"
                    data-switcher-card={game.id}
                    {...(current ? { 'data-switcher-card-current': game.id } : {})}
                    /* Текущая игра НЕ НАЖИМАЕТСЯ: нажатие «переключить на то,
                       что уже идёт» перезапускает мир без причины. */
                    disabled={current}
                    aria-current={current ? 'true' : undefined}
                    className={current ? 'cursor-default' : ''}
                    onClick={() => {
                      /*
                        В ЭФИРЕ - С ПОДТВЕРЖДЕНИЕМ, ВНЕ ЭФИРА - СРАЗУ.
                        Решение владельца 2026-09-26 (7): смена игры на ходу
                        обнуляет счёт текущего раунда, и зрители видят это
                        мгновенно. Вне эфира терять нечего, и лишний вопрос
                        там - препятствие на ровном месте.
                      */
                      if (live) {
                        setAsk(game.id);

                        return;
                      }

                      switchTo(game.id);
                    }}
                  >
                    <span className="bg-border relative block aspect-3/2 overflow-hidden rounded-lg">
                      <img
                        src={game.cover['tile']}
                        srcSet={`${game.cover['tile']} 1x, ${game.cover['tile@2x']} 2x`}
                        width={144}
                        height={96}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className={`size-full object-cover transition ${
                          current ? 'opacity-60' : 'group-hover:scale-[1.03]'
                        }`}
                      />

                      {current && (
                        <span
                          data-switcher-playing
                          /* Тёмная плашка со светлым текстом: ярко-зелёная
                           поверх обложки спорит с самой обложкой, а роль
                           «идёт сейчас» уже несёт рамка карточки. */
                          className="absolute start-1.5 top-1.5 rounded bg-black/75 px-2 py-1 text-xs font-medium text-white"
                        >
                          Идёт сейчас
                        </span>
                      )}
                    </span>

                    <span className="flex flex-col gap-2 p-3">
                      {/* Корона - СРАЗУ ЗА НАЗВАНИЕМ, а не у правого края:
                        у края она читается как отдельная кнопка, а не как
                        признак игры. */}
                      <span className="flex min-w-0 items-center gap-1">
                        <span className="text-foreground min-w-0 truncate text-sm font-semibold">
                          {ruTitle(game.title, game.id)}
                        </span>

                        {!game.free && (
                          <span className="text-warning shrink-0" title="Игра тарифа Pro">
                            <Icon name="crown" size="sm" />
                            <span className="sr-only">Игра тарифа Pro</span>
                          </span>
                        )}
                      </span>

                      {/* Категория - чипом: строкой текста того же кегля она
                        не отличается от названия ничем, кроме цвета. */}
                      {/* Не `badge-soft`: в тёмной теме он даёт контраст 3,64
                        при требуемых 4,5 - axe ловит это на каждой карточке.
                        Явная поверхность и явный цвет текста. */}
                      <span className="border-foreground/15 bg-background text-foreground/90 max-w-full truncate rounded-full border px-2 py-1 text-xs">
                        {ruTitle(
                          categories.find((item) => item.id === game.category)?.title ?? {},
                          game.category,
                        )}
                      </span>
                    </span>
                  </button>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        ПОДТВЕРЖДЕНИЕ - ТОЛЬКО В ЭФИРЕ, и оно называет, что потеряется.
        Решение владельца 2026-09-26 (7). Окно подтверждения, а не тост
        после факта: счёт раунда не восстановить, а зрители увидят смену
        мгновенно.
      */}
      <Modal
        open={ask !== null}
        onClose={() => {
          setAsk(null);
        }}
        title="Сменить игру прямо в эфире?"
        actions={
          <>
            <Button
              variant="secondary"
              data-switcher="cancel"
              onClick={() => {
                setAsk(null);
              }}
            >
              Остаться
            </Button>
            <Button
              variant="primary"
              data-switcher="confirm"
              onClick={() => {
                if (ask !== null) switchTo(ask);
              }}
            >
              Сменить игру
            </Button>
          </>
        }
      >
        Счёт текущего раунда обнулится, и зрители увидят новую игру сразу. Подключение к TikTok,
        минуты эфира и статистика останутся на месте.
      </Modal>
    </div>
  );
}
